import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  BarChart3, Users, Package, Wallet, ArrowUpRight, 
  Settings, Gift, Star, LogOut, Bell, Menu, X, Shield,
  FileCheck, MessageSquare
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

// Sub-pages
import AdminOverview from './AdminOverview';
import AdminUsers from './AdminUsers';
import AdminPackages from './AdminPackages';
import AdminDeposits from './AdminDeposits';
import AdminWithdrawals from './AdminWithdrawals';
import AdminStars from './AdminStars';
import AdminPromotions from './AdminPromotions';
import AdminSettings from './AdminSettings';
import AdminKYC from './AdminKYC';
import AdminTickets from './AdminTickets';

const LOGO_MAIN = "https://customer-assets.emergentagent.com/job_stake-blc/artifacts/qx16sddk_Screenshot__247_-removebg-preview%20(1).png";

const navItems = [
  { path: '/admin', icon: BarChart3, label: 'Overview' },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/kyc', icon: FileCheck, label: 'KYC Review', badge: 'pending_kyc' },
  { path: '/admin/packages', icon: Package, label: 'Packages' },
  { path: '/admin/deposits', icon: Wallet, label: 'Deposits', badge: 'pending_deposits' },
  { path: '/admin/withdrawals', icon: ArrowUpRight, label: 'Withdrawals', badge: 'pending_withdrawals' },
  { path: '/admin/stars', icon: Star, label: 'Star Program' },
  { path: '/admin/promotions', icon: Gift, label: 'Promotions' },
  { path: '/admin/tickets', icon: MessageSquare, label: 'Tickets', badge: 'open_tickets' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-400 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0a0500] border-r border-white/5 backdrop-blur-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-20 px-6 border-b border-white/5">
            <div className="flex items-center gap-2 text-amber-500">
              <Shield className="w-5 h-5" />
              <span className="font-medium tracking-wide">ADMIN PANEL</span>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-zinc-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-4">
            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path || 
                  (item.path !== '/admin' && location.pathname.startsWith(item.path));
                const badgeCount = item.badge ? stats[item.badge] : 0;
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-link ${isActive ? 'active' : ''} flex items-center justify-between`}
                    data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </div>
                    {badgeCount > 0 && (
                      <span className="bg-red-500 text-white text-[10px] px-1.5 rounded-full">
                        {badgeCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Admin Info */}
          <div className="p-4 border-t border-white/5">
            <div className="glass rounded-lg p-3">
              <p className="text-xs text-zinc-500">Logged in as</p>
              <p className="text-amber-400 font-medium text-sm">{user?.email}</p>
            </div>
          </div>

          {/* Logout */}
          <div className="p-4 border-t border-white/5">
            <button 
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-20 bg-black/80 border-b border-white/5 backdrop-blur-md">
          <div className="flex items-center justify-between h-full px-6">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-zinc-400 hover:text-white"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg text-white font-medium">Admin Dashboard</h1>
                <p className="text-xs text-zinc-500">System Status: <span className="text-green-400">Operational</span></p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button className="relative p-2 rounded-full glass text-zinc-400 hover:text-white">
                <Bell className="w-4 h-4" />
                {(stats.pending_deposits + stats.pending_withdrawals) > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] flex items-center justify-center text-white">
                    {stats.pending_deposits + stats.pending_withdrawals}
                  </span>
                )}
              </button>

              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-black font-bold text-sm">
                  A
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="kyc" element={<AdminKYC />} />
            <Route path="packages" element={<AdminPackages />} />
            <Route path="deposits" element={<AdminDeposits />} />
            <Route path="withdrawals" element={<AdminWithdrawals />} />
            <Route path="stars" element={<AdminStars />} />
            <Route path="promotions" element={<AdminPromotions />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="settings" element={<AdminSettings />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
